import React, { useState } from 'react';
import './SetupFlow.css';
import AppHeader from './AppHeader';
import { detectPlatform } from '../utils/platform';
import icConnect from '../images/icons/ic_connect.png';
import icCloud from '../images/icons/ic_cloud.png';
import icAdd from '../images/icons/ic_add.png';
import icDone from '../images/icons/ic_done.png';
import apiService, { API_BASE_URL } from '../services/api';
import Toast from './Toast';
import FanfareConfetti from './FanfareConfetti';
import { triggerHaptic, openExternalLink } from '../services/telegram';

function isValidSubscriptionUrl(subscriptionUrl) {
  const s = String(subscriptionUrl || '').trim();
  return s && (s.startsWith('http://') || s.startsWith('https://'));
}

const REDIRECT_BASE_URL = process.env.REACT_APP_REDIRECT_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

const HAPP_APP_STORE_LINKS = {
  global: 'https://apps.apple.com/us/app/happ-proxy-utility/id6504287215',
  russia: 'https://apps.apple.com/ru/app/happ-proxy-utility-plus/id6746188973',
};

const PLATFORM_LINKS = {
  'Windows': 'https://www.happ.su/main/ru',
  'Android': 'https://play.google.com/store/apps/details?id=com.happproxy',
  'Linux': 'https://www.happ.su/main/ru',
  'Unknown': 'https://www.happ.su/main/ru'
};

function SetupFlow({ onBack, onComplete, user, onNavigateToBuy }) {
  const [step, setStep] = useState(2); // start at install step, skip device selection
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info', duration: 3000 });
  const [installModal, setInstallModal] = useState({ open: false, url: '' });
  const platform = detectPlatform();
  const isApplePlatform = platform === 'iOS' || platform === 'macOS';
  const installLink = PLATFORM_LINKS[platform] || PLATFORM_LINKS['Unknown'];

  // --- funnel tracking helper ---
  const _funnelId = (!user?.tariff_plan || user.tariff_plan === 'trial') ? 2 : 3;
  const _trackSetup = (step) => {
    const uid = user?.userid ?? user?.id ?? null;
    const uname = user?.username ?? null;
    apiService.trackEvent({ funnel_id: _funnelId, step, user_id: uid, username: uname });
  };

  const handleInstallClick = () => {
    triggerHaptic();
    _trackSetup('install_clicked');
    setInstallModal({ open: true, url: installLink });
  };

  const handleInstallHapp = (version) => {
    triggerHaptic();
    _trackSetup('install_clicked');
    const link = HAPP_APP_STORE_LINKS[version] || HAPP_APP_STORE_LINKS.russia;
    setInstallModal({ open: true, url: link });
  };

  const handleInstallConfirm = () => {
    const { url } = installModal;
    setInstallModal({ open: false, url: '' });
    openExternalLink(url);
  };

  const calculateDaysUntilExpiry = () => {
    if (!user?.tariff_end_date) return 0;
    const endDate = new Date(user.tariff_end_date);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const copyToClipboard = async (text) => {
    if (!text || typeof document === 'undefined') return false;
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none;';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) return true;
    } catch (_) {}
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (_) {}
    }
    return false;
  };

  const handleAddSubscription = async () => {
    triggerHaptic();
    const hasSubscription = user?.tariff_end_date && new Date(user.tariff_end_date) > new Date();

    if (!hasSubscription) {
      if (onNavigateToBuy) {
        onNavigateToBuy();
      } else {
        setToast({ isOpen: true, message: 'Сначала оформите подписку. Перейдите в раздел «Купить подписку» на главном экране.', type: 'error', duration: 6000 });
      }
      return;
    }

    setToast({ isOpen: true, message: 'Получаю ключ из сервиса…', type: 'info', duration: 15000 });
    try {
      const daysUntilExpiry = calculateDaysUntilExpiry();
      if (daysUntilExpiry <= 0) {
        setToast({ isOpen: true, message: 'Подписка истекла', type: 'error', duration: 6000 });
        return;
      }

      const userId = user?.userid ?? user?.id;
      if (!userId) {
        setToast({ isOpen: true, message: 'Ошибка: пользователь не найден', type: 'error', duration: 6000 });
        return;
      }

      const keyResponse = await apiService.createKey(userId);
      let subscriptionUrl = null;
      if (typeof keyResponse === 'string') {
        subscriptionUrl = keyResponse;
      } else if (keyResponse && typeof keyResponse === 'object') {
        // Prefer stable subscription URL (subscription_token) — persists across key rotations.
        // Token may come from the key response (backend now returns it) or from user prop.
        const token = (keyResponse?.subscription_token || user?.subscription_token || '').trim();
        if (token) {
          subscriptionUrl = `${API_BASE_URL}/subscription/${token}.txt`;
        } else {
          subscriptionUrl = keyResponse.url || keyResponse.user_key || keyResponse.key || keyResponse.subscription_link || keyResponse.link;
        }
      }

      if (!subscriptionUrl || String(subscriptionUrl).trim() === '') {
        setToast({ isOpen: true, message: 'Не удалось получить ключ. Попробуйте ещё раз.', type: 'error', duration: 6000 });
        return;
      }

      const subUrl = String(subscriptionUrl).trim();
      if (!isValidSubscriptionUrl(subUrl)) {
        setToast({ isOpen: true, message: 'Ошибка: не удалось сформировать ссылку', type: 'error', duration: 5000 });
        return;
      }
      // Сначала пробуем защищённую ссылку (happ://crypt4/...); при ошибке — обычная ссылка, чтобы не блокировать пользователя
      let linkForRedirect = null;
      let usedFallback = false;
      try {
        const happLink = await apiService.getEncryptedHappLink(subUrl);
        if (happLink && (happLink.startsWith('happ://crypt4/') || happLink.startsWith('happ://crypt5/'))) {
          linkForRedirect = happLink;
        }
      } catch (_) {}
      if (!linkForRedirect) {
        linkForRedirect = subUrl;
        usedFallback = true;
      }
      const copied = await copyToClipboard(linkForRedirect.startsWith('happ://') ? linkForRedirect : subUrl);
      setToast({
        isOpen: true,
        message: usedFallback
          ? (copied ? 'Ссылка скопирована. Откройте Happ → Добавить подписку → из буфера. (Сервис шифрования временно недоступен.)' : 'Откройте Happ и добавьте подписку вручную.')
          : (copied ? 'Ссылка скопирована. Откройте Happ → Добавить подписку → из буфера.' : 'Откройте Happ и добавьте подписку вручную. Ссылка в браузере.'),
        type: 'info',
        duration: 6000,
      });
      const redirectBase = String(REDIRECT_BASE_URL || '').replace(/\/$/, '');
      const redirectUrl = `${redirectBase}/redirect?subscription_url=${encodeURIComponent(linkForRedirect)}`;
      _trackSetup('key_added');
      openExternalLink(redirectUrl);
    } catch (error) {
      const msg = error?.message || (error && String(error)) || 'Ошибка при получении ключа. Проверьте интернет и попробуйте снова.';
      setToast({ isOpen: true, message: msg, type: 'error', duration: 7000 });
    }
  };

  if (step === 1) {
    return (
      <>
        <div className="setup-flow">
          <div className="background"></div>
          <AppHeader title="Rocket VPN" />
          <div className="setup-content">
            <div className="setup-icon-container">
              <div className="setup-ripple"></div>
              <div className="setup-icon-wrapper">
                <img src={icConnect} alt="" className="setup-icon-image" />
              </div>
            </div>
            <div className="setup-text-buttons">
              <h2 className="setup-title">Настройка на {platform === 'iOS' ? 'iOS' : platform}</h2>
              <p className="setup-description">Настройка VPN происходит в 3 шага и занимает пару минут</p>
              <div className="setup-buttons">
              <button className="setup-primary-button" onClick={() => { triggerHaptic(); setStep(2); }}>
                Начать установку на этом устройстве
              </button>
              <button className="setup-secondary-button" onClick={() => { triggerHaptic(); setStep(2); }}>
                Установить на другом устройстве
              </button>
            </div>
            </div>
          </div>
        </div>
        <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} duration={toast.duration ?? 3000} onClose={() => setToast({ ...toast, isOpen: false })} />
      </>
    );
  }

  if (step === 2) {
    return (
      <>
        <div className="setup-flow">
          <div className="background"></div>
          <AppHeader title="Rocket VPN" />
          <div className="setup-content">
            <div className="setup-icon-container">
              <div className="setup-ripple"></div>
              <div className="setup-icon-wrapper">
                <img src={icCloud} alt="" className="setup-icon-image" />
              </div>
            </div>
            <div className="setup-text-buttons">
              <h2 className="setup-title">Приложение</h2>
              <p className="setup-description">Установите приложение Happ и вернитесь к этому экрану</p>
              {isApplePlatform ? (
                <div className="setup-buttons">
                  <p className="setup-version-label">Выберите версию приложения:</p>
                  <button className="setup-version-button" onClick={() => handleInstallHapp('global')}>
                    🌍 Глобальная версия
                  </button>
                  <button className="setup-version-button" onClick={() => handleInstallHapp('russia')}>
                    🇷🇺 Российская версия
                  </button>
                  <button className="setup-primary-button" onClick={() => { triggerHaptic(); _trackSetup('step_next'); setStep(3); }}>
                    Далее
                  </button>
                </div>
              ) : (
                <div className="setup-buttons-row">
                  <button className="setup-install-button" onClick={handleInstallClick}>
                    <img src={icConnect} alt="" className="button-icon-small" />
                    Установить
                  </button>
                  <button className="setup-next-button" onClick={() => { triggerHaptic(); _trackSetup('step_next'); setStep(3); }}>
                    Далее
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} duration={toast.duration ?? 3000} onClose={() => setToast({ ...toast, isOpen: false })} />
        {installModal.open && (
          <div className="install-warn-overlay" onClick={() => setInstallModal({ open: false, url: '' })}>
            <div className="install-warn-sheet" onClick={e => e.stopPropagation()}>
              <button className="install-warn-close" onClick={() => setInstallModal({ open: false, url: '' })}>✕</button>
              <h3 className="install-warn-title">Важная информация</h3>
              <p className="install-warn-text">
                После установки приложения Happ, обязательно вернитесь на этот экран и нажмите кнопку «Далее», чтобы добавить конфигурацию в приложение — без этого VPN работать не будет.
              </p>
              <button className="install-warn-btn" onClick={handleInstallConfirm}>
                Хорошо, перейти к установке
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  if (step === 3) {
    return (
      <>
        <div className="setup-flow">
          <div className="background"></div>
          <AppHeader title="Rocket VPN" />
          <div className="setup-content">
            <div className="setup-icon-container">
              <div className="setup-ripple"></div>
              <div className="setup-icon-wrapper">
                <img src={icAdd} alt="" className="setup-icon-image" />
              </div>
            </div>
            <div className="setup-text-buttons">
              <h2 className="setup-title">Подписка</h2>
              <p className="setup-description">Добавьте подписку в приложение Happ с помощью кнопки ниже</p>
              <div className="setup-buttons-row">
              <button className="setup-add-button" onClick={handleAddSubscription}>
                <img src={icConnect} alt="" className="button-icon-small" />
                Добавить
              </button>
              <button className="setup-next-button" onClick={() => { triggerHaptic(); _trackSetup('step_next_done'); setStep(4); }}>
                Далее
              </button>
            </div>
            </div>
          </div>
        </div>
        <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} duration={toast.duration ?? 3000} onClose={() => setToast({ ...toast, isOpen: false })} />
      </>
    );
  }

  if (step === 4) {
    return (
      <>
        <div className="setup-flow">
          <FanfareConfetti />
          <div className="background"></div>
          <AppHeader title="Rocket VPN" />
          <div className="setup-content">
            <div className="setup-icon-container">
              <div className="setup-success-ripple"></div>
              <div className="setup-success-icon">
                <img src={icDone} alt="" className="setup-done-image" />
              </div>
            </div>
            <div className="setup-text-buttons">
              <h2 className="setup-title">Готово!</h2>
              <p className="setup-description">Нажмите на круглую кнопку включения VPN в приложении Happ</p>
              <div className="setup-buttons">
              <button className="setup-complete-button" onClick={() => { triggerHaptic(); onComplete?.(); }}>
                Завершить настройку
              </button>
            </div>
            </div>
          </div>
        </div>
        <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} duration={toast.duration ?? 3000} onClose={() => setToast({ ...toast, isOpen: false })} />
      </>
    );
  }

  return (
    <>
      {step === 1 && (
        <div className="setup-flow">
          <div className="background"></div>
          <div className="setup-content">
            <div className="setup-icon-container">
              <div className="setup-ripple"></div>
              <div className="setup-icon-wrapper">
                <img src={icConnect} alt="" className="setup-icon-image" />
              </div>
            </div>
            <div className="setup-text-buttons">
              <h2 className="setup-title">Настройка на {platform === 'iOS' ? 'iOS' : platform}</h2>
              <p className="setup-description">Настройка VPN происходит в 3 шага и занимает пару минут</p>
              <div className="setup-buttons">
                <button className="setup-primary-button" onClick={() => { triggerHaptic(); setStep(2); }}>
                  Начать установку на этом устройстве
                </button>
                <button className="setup-secondary-button" onClick={() => { triggerHaptic(); setStep(2); }}>
                  Установить на другом устройстве
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {step === 2 && (
        <div className="setup-flow">
          <div className="background"></div>
          <div className="setup-content">
            <div className="setup-icon-container">
              <div className="setup-ripple"></div>
              <div className="setup-icon-wrapper">
                <img src={icCloud} alt="" className="setup-icon-image" />
              </div>
            </div>
            <div className="setup-text-buttons">
              <h2 className="setup-title">Приложение</h2>
              <p className="setup-description">Установите приложение Happ и вернитесь к этому экрану</p>
              {isApplePlatform ? (
                <div className="setup-buttons">
                  <p className="setup-version-label">Выберите версию приложения:</p>
                  <button className="setup-version-button" onClick={() => handleInstallHapp('global')}>
                    🌍 Глобальная версия
                  </button>
                  <button className="setup-version-button" onClick={() => handleInstallHapp('russia')}>
                    🇷🇺 Российская версия
                  </button>
                  <button className="setup-primary-button" onClick={() => { triggerHaptic(); _trackSetup('step_next'); setStep(3); }}>
                    Далее
                  </button>
                </div>
              ) : (
                <div className="setup-buttons-row">
                  <button className="setup-install-button" onClick={handleInstallClick}>
                    <img src={icConnect} alt="" className="button-icon-small" />
                    Установить
                  </button>
                  <button className="setup-next-button" onClick={() => { triggerHaptic(); _trackSetup('step_next'); setStep(3); }}>
                    Далее
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {step === 3 && (
        <div className="setup-flow">
          <div className="background"></div>
          <div className="setup-content">
            <div className="setup-icon-container">
              <div className="setup-ripple"></div>
              <div className="setup-icon-wrapper">
                <img src={icAdd} alt="" className="setup-icon-image" />
              </div>
            </div>
            <div className="setup-text-buttons">
              <h2 className="setup-title">Подписка</h2>
              <p className="setup-description">Добавьте подписку в приложение Happ с помощью кнопки ниже</p>
              <div className="setup-buttons-row">
                <button className="setup-add-button" onClick={handleAddSubscription}>
                  <img src={icConnect} alt="" className="button-icon-small" />
                  Добавить
                </button>
                <button className="setup-next-button" onClick={() => { triggerHaptic(); _trackSetup('step_next_done'); setStep(4); }}>
                  Далее
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {step === 4 && (
        <div className="setup-flow">
          <FanfareConfetti />
          <div className="background"></div>
          <div className="setup-content">
            <div className="setup-icon-container">
              <div className="setup-success-ripple"></div>
              <div className="setup-success-icon">
                <img src={icDone} alt="" className="setup-done-image" />
              </div>
            </div>
            <div className="setup-text-buttons">
              <h2 className="setup-title">Готово!</h2>
              <p className="setup-description">Нажмите на круглую кнопку включения VPN в приложении Happ</p>
              <div className="setup-buttons">
                <button className="setup-complete-button" onClick={() => { triggerHaptic(); onComplete?.(); }}>
                  Завершить настройку
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        duration={toast.duration ?? 3000}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </>
  );
}

export default SetupFlow;

